/**
 * Analys av användarens nuvarande elbeteende:
 * - Hur mycket sparar/förlorar de på sin nuvarande timing av förbrukning?
 * - Förbrukningsmönster per timme på dygnet vs spotpris
 *
 * Data-agnostiskt — använder bara HourPoint som finns oavsett om datan
 * kommer från Tibber, CSV eller annan nätägare.
 */

import type { HourPoint } from "./types";

export interface TimingAnalysis {
  /** Faktiskt viktat snitt-importpris (kr/kWh, exkl skatt) */
  weightedAvgSpotPrice: number;
  /** Naivt snittpris om förbrukningen vore jämnt fördelad (kr/kWh) */
  naiveAvgSpotPrice: number;
  /** Skillnad i kr/kWh (positivt = bättre timing än snitt, negativt = sämre) */
  pricePerKWhDelta: number;
  /** Total kr/år sparat eller förlorat via nuvarande timing */
  annualValueSek: number;
  /** Total årlig import (kWh) — projecerad till 12 månader */
  annualImportKWh: number;
  /** Score 0-100, där 50 = neutral, högre = bättre timing */
  score: number;
}

export interface HourlyProfile {
  hour: number;
  avgImportKWh: number;
  avgSpotPrice: number;
  importShare: number;
}

/**
 * Beräknar hur bra användaren timear sin elförbrukning. Jämför viktat
 * snitt-importpris (vad de FAKTISKT betalar i spotpris) med naivt snitt
 * (vad de skulle betalat om förbrukningen vore jämn över alla timmar).
 *
 * Bara spotpris-delen analyseras — energiskatt, moms och transmission
 * är desamma per kWh oavsett timme och påverkar inte timing-skickligheten.
 */
export function calculateTimingAnalysis(
  hours: HourPoint[],
  monthCount: number,
): TimingAnalysis {
  if (hours.length === 0) {
    return {
      weightedAvgSpotPrice: 0,
      naiveAvgSpotPrice: 0,
      pricePerKWhDelta: 0,
      annualValueSek: 0,
      annualImportKWh: 0,
      score: 50,
    };
  }

  let totalImport = 0;
  let weightedSum = 0;
  let priceSum = 0;
  let priceCount = 0;

  for (const h of hours) {
    if (h.importKWh > 0) {
      totalImport += h.importKWh;
      weightedSum += h.spotPrice * h.importKWh;
    }
    priceSum += h.spotPrice;
    priceCount += 1;
  }

  const weightedAvg = totalImport > 0 ? weightedSum / totalImport : 0;
  const naiveAvg = priceCount > 0 ? priceSum / priceCount : 0;
  // Positivt delta = användaren betalar MINDRE än snitt (bra timing)
  const pricePerKWhDelta = naiveAvg - weightedAvg;

  const monthsFactor = 12 / Math.max(1, monthCount);
  const annualImport = totalImport * monthsFactor;
  // VAT applicerar på spotpris-delen också, så multiplicera med 1.25 för
  // att få verkligt kr/år-värde användaren ser på elräkningen
  const annualValueSek = pricePerKWhDelta * annualImport * 1.25;

  // Score: räknar relativ position. Om delta = 0 → 50. Om delta = naiveAvg
  // (gratis genom perfekt timing) → 100. Om delta = -naiveAvg (alltid på topp) → 0.
  // Beräkningen är robust mot låga priser via Math.max.
  const baseline = Math.max(0.1, naiveAvg);
  const rawScore = 50 + (pricePerKWhDelta / baseline) * 50;
  const score = Math.max(0, Math.min(100, rawScore));

  return {
    weightedAvgSpotPrice: weightedAvg,
    naiveAvgSpotPrice: naiveAvg,
    pricePerKWhDelta,
    annualValueSek,
    annualImportKWh: annualImport,
    score,
  };
}

/**
 * Extraherar lokal klocktimme (00-23) direkt från ISO-string med offset.
 * Mer robust än new Date().getHours() som ger systemtidszon, vilket kan
 * vara fel i tester eller om appen körs från annan region än datakällan.
 */
function getHourFromISO(iso: string): number {
  return parseInt(iso.substring(11, 13), 10);
}

/**
 * Returnerar 24-positions profil med genomsnittlig import och spotpris
 * per klockslag. Använt för visualisering — visar om användarens förbruk-
 * ningstoppar sammanfaller med pristoppar.
 */
export function calculateHourlyProfile(hours: HourPoint[]): HourlyProfile[] {
  const buckets: { import: number; price: number; count: number }[] = Array.from(
    { length: 24 },
    () => ({ import: 0, price: 0, count: 0 }),
  );

  let totalImport = 0;
  for (const h of hours) {
    const hourOfDay = getHourFromISO(h.from);
    const bucket = buckets[hourOfDay]!;
    bucket.import += h.importKWh;
    bucket.price += h.spotPrice;
    bucket.count += 1;
    totalImport += h.importKWh;
  }

  return buckets.map((b, hour) => ({
    hour,
    avgImportKWh: b.count > 0 ? b.import / b.count : 0,
    avgSpotPrice: b.count > 0 ? b.price / b.count : 0,
    importShare: totalImport > 0 ? b.import / totalImport : 0,
  }));
}

/**
 * Klassificering av timing-skicklighet baserat på score.
 */
export function classifyTimingScore(score: number): {
  label: string;
  description: string;
  level: "good" | "neutral" | "poor";
} {
  if (score >= 58) {
    return {
      label: "Bra timing",
      description:
        "Du köper redan en betydande andel av din el under lågpristimmar. Batteriet kompletterar din befintliga skicklighet, men marginalen är mindre.",
      level: "good",
    };
  }
  if (score <= 42) {
    return {
      label: "Försämrad timing",
      description:
        "Din förbrukning toppar ofta samtidigt som spotpriset. Batteriet har stor potential att förbättra detta — eller alternativt, smart styrning av befintliga apparater (värmepump, laddbox, varmvattenberedare) kan ge stora vinster utan investering.",
      level: "poor",
    };
  }
  return {
    label: "Neutral timing",
    description:
      "Din förbrukning följer ungefär ett genomsnittligt mönster över dygnet. Batteriet ger ren marginalvinst genom att aktivt flytta förbrukning till lågprisinstunder.",
    level: "neutral",
  };
}
